import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Shield, 
  Lock, 
  Eye, 
  EyeOff, 
  Brain, 
  FileText, 
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Info,
  History,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Scale,
  FileWarning,
  Globe,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecuritySettingsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    encrypt_files_at_rest: true,
    encrypt_content_preview: true,
    anonymize_for_ai: false,
    audit_logging: true,
    auto_delete_after_days: null
  });
  const [encryptionStatus, setEncryptionStatus] = useState(null);
  const [aiNotice, setAiNotice] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dataSent: false,
    openaiPolicy: false,
    privilege: false,
    compliance: false,
    security: false,
    recommendations: false
  });

  useEffect(() => {
    fetchSecurityData();
  }, [token]);

  const fetchSecurityData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [settingsRes, encryptionRes, noticeRes] = await Promise.all([
        fetch(`${API_URL}/api/security/settings`, { headers }),
        fetch(`${API_URL}/api/security/encryption-status`, { headers }),
        fetch(`${API_URL}/api/security/ai-privacy-notice`)
      ]);

      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (encryptionRes.ok) setEncryptionStatus(await encryptionRes.json());
      if (noticeRes.ok) setAiNotice(await noticeRes.json());
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/security/audit-log?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/security/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (res.ok) {
        setSettings(prev => ({ ...prev, [key]: value }));
        toast.success('Security settings updated');
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const acknowledgeAiNotice = async () => {
    try {
      const res = await fetch(`${API_URL}/api/security/acknowledge-ai-notice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('AI privacy notice acknowledged');
      }
    } catch (error) {
      toast.error('Failed to acknowledge notice');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const CollapsibleSection = ({ id, icon: Icon, title, children, iconColor = "text-navy" }) => (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span className="font-medium">{title}</span>
        </div>
        {expandedSections[id] ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="p-4 border-t bg-white">
          {children}
        </div>
      )}
    </div>
  );

  const ExternalLinkButton = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-bronze hover:underline"
    >
      {children}
      <ExternalLink className="w-3 h-3" />
    </a>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <Shield className="w-12 h-12 text-navy/20 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading security settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="security-settings-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-navy" />
                <h1 className="text-xl font-heading font-bold">Security & Privacy</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Encryption Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle>Encryption Status</CardTitle>
                  <CardDescription>Your data is protected with industry-standard encryption</CardDescription>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Active
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {encryptionStatus && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Algorithm</p>
                  <p className="font-medium text-sm">{encryptionStatus.algorithm}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Key Derivation</p>
                  <p className="font-medium text-sm">{encryptionStatus.key_derivation}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Transit Security</p>
                  <p className="font-medium text-sm">{encryptionStatus.features?.transit}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Iterations</p>
                  <p className="font-medium text-sm">{encryptionStatus.iterations?.toLocaleString()}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Privacy Settings</CardTitle>
                <CardDescription>Control how your data is handled and protected</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Encrypt Files at Rest
                </Label>
                <p className="text-sm text-muted-foreground">
                  Encrypt uploaded documents using AES-256 encryption
                </p>
              </div>
              <Switch
                checked={settings.encrypt_files_at_rest}
                onCheckedChange={(checked) => updateSetting('encrypt_files_at_rest', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Encrypt Content Preview
                </Label>
                <p className="text-sm text-muted-foreground">
                  Encrypt extracted text content stored in the database
                </p>
              </div>
              <Switch
                checked={settings.encrypt_content_preview}
                onCheckedChange={(checked) => updateSetting('encrypt_content_preview', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  Anonymize for AI Analysis
                  <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">Recommended for Sensitive Data</Badge>
                </Label>
                <p className="text-sm text-muted-foreground">
                  Automatically redact SSN, phone numbers, emails, and other PII before sending to AI
                </p>
              </div>
              <Switch
                checked={settings.anonymize_for_ai}
                onCheckedChange={(checked) => updateSetting('anonymize_for_ai', checked)}
                disabled={saving}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Audit Logging
                </Label>
                <p className="text-sm text-muted-foreground">
                  Track document access, AI analysis, and exports for compliance
                </p>
              </div>
              <Switch
                checked={settings.audit_logging}
                onCheckedChange={(checked) => updateSetting('audit_logging', checked)}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* Comprehensive AI Privacy Notice */}
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Brain className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  AI Data Handling & Privacy Notice
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </CardTitle>
                <CardDescription>
                  Version {aiNotice?.version || '2.0'} • Last updated: {aiNotice?.last_updated || 'December 2025'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {aiNotice?.summary || 'Loading...'}
              </p>
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-3">
              {/* What Data Is Sent */}
              <CollapsibleSection id="dataSent" icon={FileWarning} title="What Data Is Sent to AI" iconColor="text-blue-600">
                {aiNotice?.data_sent_to_ai && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.data_sent_to_ai.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                        <h5 className="font-medium text-red-800 mb-2">Data Sent:</h5>
                        <ul className="text-sm text-red-700 space-y-1">
                          {aiNotice.data_sent_to_ai.items?.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-red-500 mt-1">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <h5 className="font-medium text-green-800 mb-2">NOT Sent:</h5>
                        <ul className="text-sm text-green-700 space-y-1">
                          {aiNotice.data_sent_to_ai.not_sent?.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* OpenAI Data Policy */}
              <CollapsibleSection id="openaiPolicy" icon={Globe} title="OpenAI Data Retention Policy" iconColor="text-purple-600">
                {aiNotice?.openai_data_policy && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.openai_data_policy.description}</p>
                    
                    <ul className="text-sm space-y-2">
                      {aiNotice.openai_data_policy.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Official OpenAI Resources:</p>
                      <div className="flex flex-wrap gap-3">
                        {aiNotice.openai_data_policy.links?.map((link, i) => (
                          <ExternalLinkButton key={i} href={link.url}>
                            {link.text}
                          </ExternalLinkButton>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Attorney-Client Privilege */}
              <CollapsibleSection id="privilege" icon={Scale} title="Attorney-Client Privilege Considerations" iconColor="text-amber-600">
                {aiNotice?.attorney_client_privilege && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium mb-2">⚠️ Important Legal Considerations</p>
                      <p className="text-sm text-amber-700">{aiNotice.attorney_client_privilege.description}</p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium mb-2">Key Points:</h5>
                      <ul className="text-sm space-y-2">
                        {aiNotice.attorney_client_privilege.items?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-medium mb-2">Recommendations:</h5>
                      <ul className="text-sm space-y-2">
                        {aiNotice.attorney_client_privilege.recommendations?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">ABA Resources:</p>
                      <div className="flex flex-wrap gap-3">
                        {aiNotice.attorney_client_privilege.links?.map((link, i) => (
                          <ExternalLinkButton key={i} href={link.url}>
                            {link.text}
                          </ExternalLinkButton>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Regulatory Compliance */}
              <CollapsibleSection id="compliance" icon={Building2} title="Regulatory Compliance Considerations" iconColor="text-indigo-600">
                {aiNotice?.compliance_considerations && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.compliance_considerations.description}</p>
                    
                    <div className="space-y-3">
                      {aiNotice.compliance_considerations.regulations?.map((reg, i) => (
                        <div key={i} className="bg-muted/30 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="font-semibold">{reg.name}</Badge>
                            <span className="text-xs text-muted-foreground">— {reg.applies_to}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{reg.consideration}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Our Security Measures */}
              <CollapsibleSection id="security" icon={Shield} title="Our Security Measures" iconColor="text-green-600">
                {aiNotice?.security_measures && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.security_measures.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-2">
                      {aiNotice.security_measures.items?.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Best Practices */}
              <CollapsibleSection id="recommendations" icon={Info} title="Best Practices for Confidential Documents" iconColor="text-navy">
                {aiNotice?.recommendations && (
                  <div>
                    <ul className="text-sm space-y-2">
                      {aiNotice.recommendations.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-bronze mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CollapsibleSection>
            </div>

            {/* Acknowledge Button */}
            <div className="flex justify-end pt-4 border-t border-amber-200">
              <Button 
                onClick={acknowledgeAiNotice}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                I Have Read and Understand This Notice
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <History className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle>Audit Log</CardTitle>
                  <CardDescription>Recent security events and document access</CardDescription>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setShowAuditLog(!showAuditLog);
                  if (!showAuditLog) fetchAuditLogs();
                }}
              >
                {showAuditLog ? 'Hide' : 'View'} Logs
              </Button>
            </div>
          </CardHeader>
          {showAuditLog && (
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No audit logs yet</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {log.event_type.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-muted-foreground">
                          {log.resource_type}: {log.resource_id.slice(0, 8)}...
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
}
